import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Spinner } from '../../components/ui/spinner';
import { fmtBRL, fmtCompetencia } from '../../lib/format';

type Aba = 'contratos' | 'associados' | 'aptos' | 'ciclos';

export function RelatoriosPage() {
  const [aba, setAba] = useState<Aba>('contratos');
  const [competencia, setCompetencia] = useState('2026-04');

  const contratos = useQuery({
    queryKey: ['rel-contratos', competencia],
    queryFn: () => api<any>(`/relatorios/contratos-mes?competencia=${competencia}`),
    enabled: aba === 'contratos',
  });
  const associados = useQuery({
    queryKey: ['rel-associados', competencia],
    queryFn: () => api<any>(`/relatorios/associados-mes?competencia=${competencia}`),
    enabled: aba === 'associados',
  });
  const aptos = useQuery({
    queryKey: ['rel-aptos'],
    queryFn: () => api<any>('/relatorios/aptos-renovar'),
    enabled: aba === 'aptos',
  });
  const ciclos = useQuery({
    queryKey: ['rel-ciclos'],
    queryFn: () => api<any>('/relatorios/ciclos-mensais'),
    enabled: aba === 'ciclos',
  });

  const abas: { key: Aba; label: string }[] = [
    { key: 'contratos', label: 'Contratos do mês' },
    { key: 'associados', label: 'Associados do mês' },
    { key: 'aptos', label: 'Aptos a renovar' },
    { key: 'ciclos', label: 'Ciclos mensais' },
  ];

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Relatórios</h1>
        <p className="text-sm text-muted-foreground">Recortes por competência e renovação</p>
      </div>

      {/* Abas */}
      <div className="mb-4 flex gap-2 border-b">
        {abas.map((a) => (
          <button key={a.key}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${aba === a.key ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
            onClick={() => setAba(a.key)}>
            {a.label}
          </button>
        ))}
      </div>

      {/* Seletor de competência (não nas abas de aptos) */}
      {aba !== 'aptos' && (
        <div className="mb-4 flex items-center gap-2">
          <label className="text-sm text-muted-foreground">Competência:</label>
          <Input type="month" value={competencia} onChange={(e) => setCompetencia(e.target.value)} className="w-44" />
        </div>
      )}

      {/* CONTRATOS DO MÊS */}
      {aba === 'contratos' && (
        <Card>
          <CardHeader>
            <CardTitle>
              Contratos solicitados em {fmtCompetencia(competencia)}
              {contratos.data && <span className="ml-2 text-sm font-normal text-muted-foreground">{contratos.data.total} contratos · {fmtBRL(contratos.data.valorTotal)}</span>}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {contratos.isLoading ? <div className="flex justify-center p-8"><Spinner /></div> : (
              <table className="w-full text-sm">
                <thead><tr className="border-b text-left text-gray-500">
                  <th className="py-2">Contrato</th><th className="py-2">Associado</th>
                  <th className="py-2 text-right">Valor Total</th><th className="py-2">Status</th>
                </tr></thead>
                <tbody>
                  {contratos.data?.items.map((c: any) => (
                    <tr key={c.id} className="border-b">
                      <td className="py-2"><Link to={`/contratos/${c.contratoId}`} className="text-primary hover:underline">{c.numeroContrato}</Link></td>
                      <td className="py-2">{c.associadoNome}</td>
                      <td className="py-2 text-right">{fmtBRL(c.mensalidade)}</td><td className="py-2 text-xs">{c.agente}</td>
                      <td className="py-2">{c.statusFinanceiro}</td>
                    </tr>
                  ))}
                  {contratos.data?.items.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-gray-400">Nenhum contrato neste mês.</td></tr>}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      )}

      {/* ASSOCIADOS DO MÊS */}
      {aba === 'associados' && (
        <Card>
          <CardHeader>
            <CardTitle>
              Associados que entraram em {fmtCompetencia(competencia)}
              {associados.data && <span className="ml-2 text-sm font-normal text-muted-foreground">{associados.data.total} novos</span>}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {associados.isLoading ? <div className="flex justify-center p-8"><Spinner /></div> : (
              <table className="w-full text-sm">
                <thead><tr className="border-b text-left text-gray-500">
                  <th className="py-2">Nome</th><th className="py-2">CPF</th>
                  <th className="py-2">Matrícula</th><th className="py-2">Status</th>
                </tr></thead>
                <tbody>
                  {associados.data?.items.map((a: any) => (
                    <tr key={a.id} className="border-b">
                      <td className="py-2"><Link to={`/associados/${a.id}`} className="text-primary hover:underline">{a.nome}</Link></td>
                      <td className="py-2">{a.documentoMask}</td>
                      <td className="py-2">{a.matricula}</td>
                      <td className="py-2">{a.status}</td>
                    </tr>
                  ))}
                  {associados.data?.items.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-gray-400">Nenhum associado entrou neste mês.</td></tr>}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      )}

      {/* APTOS A RENOVAR */}
      {aba === 'aptos' && (
        <Card>
          <CardHeader>
            <CardTitle>
              Aptos a renovar
              {aptos.data && <span className="ml-2 text-sm font-normal text-muted-foreground">{aptos.data.total} ciclos em ponto de renovação (2/3 ou 3/4)</span>}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {aptos.isLoading ? <div className="flex justify-center p-8"><Spinner /></div> : (
              <table className="w-full text-sm">
                <thead><tr className="border-b text-left text-gray-500">
                  <th className="py-2">Contrato</th><th className="py-2">Associado</th>
                  <th className="py-2 text-center">Progresso</th><th className="py-2 text-right">Mensalidade</th><th className="py-2">Agente</th>
                </tr></thead>
                <tbody>
                  {aptos.data?.items.map((c: any) => (
                    <tr key={c.id} className="border-b">
                      <td className="py-2"><Link to={`/contratos/${c.contratoId}`} className="text-primary hover:underline">{c.numeroContrato}</Link></td>
                      <td className="py-2">{c.associadoNome}</td>
                      <td className="py-2 text-center font-semibold">{c.progresso}</td>
                      <td className="py-2 text-right">{fmtBRL(c.mensalidade)}</td><td className="py-2 text-xs">{c.agente}</td>
                    </tr>
                  ))}
                  {aptos.data?.items.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-gray-400">Nenhum ciclo apto.</td></tr>}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      )}

      {aba === 'ciclos' && (
        <Card>
          <CardHeader>
            <CardTitle>
              Ciclos mensais
              {ciclos.data && <span className="ml-2 text-sm font-normal text-muted-foreground">{ciclos.data.levas.length} levas</span>}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="mb-3 rounded border border-amber-300 bg-amber-50 dark:bg-amber-950 dark:border-amber-800 p-2 text-xs text-amber-800 dark:text-amber-300">
              Cada leva agrupa os ciclos abertos no mês. "Renovações" = abertura de ciclo seguinte (ciclo nº2+). Adesões = entradas novas (ciclo nº1).
            </div>
            {ciclos.isLoading ? <div className="flex justify-center p-8"><Spinner /></div> : (
              <table className="w-full text-sm">
                <thead><tr className="border-b text-left text-gray-500">
                  <th className="py-2">Leva</th><th className="py-2">Mês</th>
                  <th className="py-2 text-right">Total de ciclos</th>
                  <th className="py-2 text-right">Adesões</th><th className="py-2 text-right">Renovações</th>
                </tr></thead>
                <tbody>
                  {ciclos.data?.levas.map((l: any) => (
                    <tr key={l.mes} className="border-b">
                      <td className="py-2 font-medium">Ciclo {l.cicloOperacional}</td>
                      <td className="py-2">{fmtCompetencia(l.mes)}</td>
                      <td className="py-2 text-right">{l.totalCiclos}</td>
                      <td className="py-2 text-right text-blue-700 dark:text-blue-400">{l.adesoes}</td>
                      <td className="py-2 text-right text-green-700 dark:text-green-400 font-semibold">{l.renovacoes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
